/**
 * The feedback form body: category, summary, details, attachments and an
 * optional reply email.
 *
 * Purely presentational — all state lives in `FeedbackPanel`.
 */

import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { FEEDBACK_CATEGORY_META } from './feedbackCategories';
import { FeedbackDropzone } from './FeedbackDropzone';
import { isValidEmail, MAX_EMAIL_LENGTH } from './feedbackValidation';
import { FEEDBACK_CATEGORIES } from '../../types/feedback';
import type { FeedbackCategory, FeedbackConfig } from '../../types/feedback';

export interface FeedbackFormValue {
  category: FeedbackCategory;
  summary: string;
  details: string;
  email: string;
  files: File[];
}

interface FeedbackFormProps {
  value: FeedbackFormValue;
  onChange: (value: FeedbackFormValue) => void;
  config: FeedbackConfig;
  disabled?: boolean;
}

export function FeedbackForm({ value, onChange, config, disabled }: FeedbackFormProps) {
  const { t } = useTranslation('feedback');
  const meta = FEEDBACK_CATEGORY_META[value.category];

  const update = (patch: Partial<FeedbackFormValue>) => onChange({ ...value, ...patch });
  const emailInvalid = !isValidEmail(value.email);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        size="small"
        fullWidth
        label={t('category')}
        value={value.category}
        disabled={disabled}
        onChange={(event) =>
          // Switching category never discards what the user already typed —
          // only the labels and placeholders change.
          update({ category: event.target.value as FeedbackCategory })
        }
        slotProps={{
          select: {
            renderValue: (selected) => {
              const Icon = FEEDBACK_CATEGORY_META[selected as FeedbackCategory].icon;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon fontSize="small" />
                  {t(FEEDBACK_CATEGORY_META[selected as FeedbackCategory].labelKey)}
                </Box>
              );
            },
          },
        }}
      >
        {FEEDBACK_CATEGORIES.map((category) => {
          const Icon = FEEDBACK_CATEGORY_META[category].icon;
          return (
            <MenuItem key={category} value={category}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              {t(FEEDBACK_CATEGORY_META[category].labelKey)}
            </MenuItem>
          );
        })}
      </TextField>

      <TextField
        size="small"
        fullWidth
        label={t('fields.summary')}
        required={meta.fieldsRequired}
        placeholder={t(meta.summaryPlaceholderKey)}
        value={value.summary}
        disabled={disabled}
        // Keep the label shrunk so the category-specific placeholder is visible
        // immediately; MUI otherwise hides it behind the label until focus.
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { maxLength: config.max_summary_length },
        }}
        onChange={(event) => update({ summary: event.target.value })}
      />

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={5}
        maxRows={10}
        label={t(meta.detailsLabelKey)}
        required={meta.fieldsRequired}
        placeholder={t(meta.detailsPlaceholderKey)}
        value={value.details}
        disabled={disabled}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { maxLength: config.max_details_length },
        }}
        onChange={(event) => update({ details: event.target.value })}
      />

      <FeedbackDropzone
        files={value.files}
        onChange={(files) => update({ files })}
        config={config}
        disabled={disabled}
      />

      <TextField
        size="small"
        fullWidth
        type="email"
        autoComplete="email"
        label={t('fields.email')}
        placeholder={t('placeholders.email')}
        value={value.email}
        disabled={disabled}
        error={emailInvalid}
        helperText={t(emailInvalid ? 'errors.emailInvalid' : 'hints.email')}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { maxLength: MAX_EMAIL_LENGTH },
        }}
        onChange={(event) => update({ email: event.target.value })}
      />
    </Box>
  );
}
